import { useRef, useEffect, useState } from 'react'
import { AdminTableProps } from '../../models'
import { useAppSelector } from '../../../../app/hooks'
import { getRoomResults } from '../../utils'
import { create, update, menuValues } from '../../constants'

import {
  selectedLmsAdminSearch,
  selectedLmsLocationSearch,
  selectedLmsStatus,
} from '../../lmsSlice'
import '../../lms.scss'
import ProvenanceView from '../../../../components/ProvenanceView'
import { Box, IconButton, useTheme } from '@mui/material'
import { SUPER_ADMIN_DELETE } from '../../../../utils/roles'
import KeycloakService from '../../../../utils/keycloakService'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import '../../../administration/admin.scss'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
import { Archive } from './Archive'
import {
  activeText,
  emptyString,
  inactiveText,
} from '../../../Radiology/Constants/constants'
import { radiologyColor } from '../../../Radiology/Utils/RadiologyColors'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import CustomHeader from '../../../Radiology/components/RadiologyModel/CustomHeader'
import { ColDef } from 'ag-grid-community'
import MedAgDataGrid from '../../../../components/MedAgDataGrid'
const styles = () => ({
  centerHeader: {
    color: radiologyColor?.white,
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
    },
  },
  nameButton: {
    backgroundColor: '#0000',
    border: 'none',
    color: 'blue',
    cursor: 'pointer',
    padding: '0px',
    textAlign: 'left',
    textDecoration: 'underline',
  },
})

const Rooms = ({ data, onEdit }: AdminTableProps) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const searchParam = useAppSelector(selectedLmsAdminSearch)
  const locationSearch = useAppSelector(selectedLmsLocationSearch)
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [tableData, setTableData] = useState<any>(data)
  const [roomData, setRoomData] = useState<any>({})
  const status = useAppSelector(selectedLmsStatus)
  let filtered = getRoomResults(tableData, searchParam, locationSearch)
  filtered = filtered?.filter((d: any) => {
    const roomStatus = d?.active ? activeText : inactiveText
    return status === roomStatus
  })
  const sampleTableContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const resizeHandler = () => {
      const container = sampleTableContainerRef.current
      if (container) {
        const availableHeight: any =
          window.innerHeight - container.getBoundingClientRect().top
        container.style.maxHeight = `${availableHeight - 20}px`
      }
    }

    window.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [])

  function handleDeleteDialogOpen(d: any) {
    setDeleteDialog(true)
    setRoomData(d)
  }

  function handleCloseDeleteDialog() {
    setDeleteDialog(false)
    setRoomData(null)
  }

  useEffect(() => {
    setTableData(data)
  }, [data, status])
  const renderName = (params: any) => {
    return (
      <button
        onClick={() => onEdit(params?.data?.id)}
        className={classes?.nameButton}
      >
        {params?.data?.room_name}
      </button>
    )
  }
  const createProvenanceView = (params: any) => {
    return (
      <ProvenanceView
        mode={create}
        date={params?.data?.date_created ?? ''}
        name={params?.data?.created_by ?? '-'}
      />
    )
  }
  const updatedProvenanceView = (params: any) => {
    return (
      <ProvenanceView
        mode={update}
        date={params?.data?.date_changed ?? ''}
        name={params?.data?.updated_by ?? '-'}
      />
    )
  }
  const RenderActions = (params: any) => {
    return (
      <div className='set-action-icons'>
        {params?.data?.active ? (
          <IconButton
            title='Archive'
            size='small'
            onClick={() =>
              Archive(params?.data, setTableData, menuValues?.Room)
            }
            disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
          >
            <ArchiveOutlinedIcon fontSize='small' />
          </IconButton>
        ) : (
          <IconButton
            title='Un-Archive'
            size='small'
            onClick={() =>
              Archive(params?.data, setTableData, menuValues?.Room)
            }
            disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
          >
            <UnarchiveOutlinedIcon fontSize='small' />
          </IconButton>
        )}
        <IconButton
          title='Delete'
          size='small'
          onClick={() => handleDeleteDialogOpen(params?.data)}
          disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
        >
          <DeleteOutlined fontSize='small' />
        </IconButton>
      </div>
    )
  }

  const columnDefs: ColDef[] = [
    {
      headerName: 'SeqNo',
      field: 'sequence_number',
      minWidth: 90,
      flex: 1,
      sortable: true,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => params?.data?.sequence_number ?? '-',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Id',
      field: 'id',
      minWidth: 90,
      flex: 1,
      sortable: true,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => params?.data?.id ?? '-',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Room Name',
      field: 'room_number',
      minWidth: 300,
      sortable: false,
      filter: false,
      cellRenderer: renderName,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 0.5,
        display: 'flex',
        alignItems: 'center',
      },
      wrapText: true,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Ward Name',
      field: 'ward_name',
      minWidth: 300,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) =>
        params?.data?.ward_detail?.ward_name ?? '-',
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        whiteSpace: 'normal !important',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
      },
      wrapText: true,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'floor',
      field: 'floor_number',
      minWidth: 90,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => params?.data?.floor_number ?? emptyString,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Location',
      field: 'location_id',
      minWidth: 130,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) =>
        params?.data?.ward_detail?.location_id ?? '-',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Actions',
      field: 'id',
      minWidth: 140,
      headerClass: classes?.centerHeader,
      cellRenderer: RenderActions,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Created By',
      field: 'created_by',
      minWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: createProvenanceView,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal !important',
        overflowWrap: 'break-word',
        lineHeight: 1.2,
      },
      wrapText: true,
    },
    {
      headerName: 'Updated By',
      field: 'updated_by',
      minWidth: 130,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: updatedProvenanceView,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal !important',
        overflowWrap: 'break-word',
        lineHeight: 1.2,
      },
      wrapText: true,
    },
  ]

  return (
    <>
      <Box height={'100%'} width={'100%'}>
        <MedAgDataGrid
          tableHeight={'100%'}
          rowData={filtered}
          columnDefs={columnDefs}
          pagination={false}
          exportIcon={false}
          searchField={false}
          headerHeight={30}
          rowHeight={40}
          localSearch={false}
          enableCellTextSelection={true}
          loading={status === 'loading' ? true : false}
        />
      </Box>
      {deleteDialog && (
        <DeleteConfirmationDialog
          openDialog={deleteDialog}
          closeDialog={handleCloseDeleteDialog}
          data={roomData}
          name={roomData?.room_name}
          setData={setTableData}
          item={menuValues?.Room}
        />
      )}
    </>
  )
}

export default Rooms
